// Helpers for distinguishing media types (image vs video vs PDF vs other
// document) by URL/extension. Used by every gallery in the app so PDF
// uploads degrade gracefully into a branded "open PDF" tile instead of a
// broken <img> rendering.

const VIDEO_EXTS = ['mp4', 'webm', 'mov', 'm4v', 'ogv', 'avi', 'wmv', 'mkv', 'flv', 'mpg', 'mpeg', '3gp'];
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'heic', 'heif', 'bmp', 'tif', 'tiff', 'avif'];
const PDF_EXTS = ['pdf'];
const DOC_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx', 'txt', 'odt', 'ods', 'odp', 'rtf', 'md', 'log'];
const CAD_EXTS = ['dwg', 'dxf'];

export function mediaExt(src) {
  if (!src || typeof src !== 'string') return '';
  // Strip query/hash
  const clean = src.split('?')[0].split('#')[0];
  const dot = clean.lastIndexOf('.');
  return dot === -1 ? '' : clean.slice(dot + 1).toLowerCase();
}

export function isVideoUrl(src) {
  return VIDEO_EXTS.includes(mediaExt(src));
}

export function isImageUrl(src) {
  return IMAGE_EXTS.includes(mediaExt(src));
}

export function isPdfUrl(src) {
  return PDF_EXTS.includes(mediaExt(src));
}

export function isDocUrl(src) {
  return DOC_EXTS.includes(mediaExt(src));
}

export function isCadUrl(src) {
  return CAD_EXTS.includes(mediaExt(src));
}

// Coarse type for switch statements in gallery components.
export function mediaKind(src) {
  if (isImageUrl(src)) return 'image';
  if (isVideoUrl(src)) return 'video';
  if (isPdfUrl(src)) return 'pdf';
  if (isCadUrl(src)) return 'cad';
  if (isDocUrl(src)) return 'doc';
  return 'other';
}

// Given a video URL, return the URL where a matching sidecar poster JPG
// (generated at upload time) would live. Browsers that fail to render the
// first video frame will fall back to the <video poster=""> image.
export function posterFor(src) {
  if (!isVideoUrl(src)) return null;
  return src.replace(/\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i, '.jpg');
}

// Friendly filename ("contract-acme.pdf" → "contract-acme.pdf"). Strips
// directory + query. Used as fallback caption for PDF/doc tiles.
export function fileName(src) {
  if (!src || typeof src !== 'string') return '';
  const clean = src.split('?')[0].split('#')[0];
  const slash = clean.lastIndexOf('/');
  return slash === -1 ? clean : clean.slice(slash + 1);
}

// Accept attribute presets. Galleries that previously accepted only
// images/videos should switch to ACCEPT_GALLERY so PDFs flow through too.
// ACCEPT_ANY = everything we recognise (images, videos, PDFs, office docs).
export const ACCEPT_IMAGE_OR_VIDEO = 'image/*,video/*';
export const ACCEPT_GALLERY = 'image/*,video/*,.pdf,application/pdf,.txt,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx';
export const ACCEPT_DOCUMENT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.md,.rtf,application/pdf';
export const ACCEPT_ANY = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.md,.rtf,.heic,.heif,.tif,.tiff,.avif,application/pdf';
